import { format } from 'date-fns';
import { buildCurrencyFormatter } from './helpers';

export const onlyNumbers = (value?: string) => {
  if (value == null) return value;
  return value.replace(/[^\d]/g, '');
};

export const onlyNumbersWithDecimals = (value?: string) => {
  if (value == null) return value;
  return value.replace(/[^\d.]/g, '');
};

export const onlyAlphaNumeric = (value?: string) => {
  if (value == null) return value;
  return value.replace(/[^\w]/g, '');
};

export const onlyTwoDecimals = (value?: string) => {
  if (value == null) return value;
  const [integer, decimal] = (onlyNumbersWithDecimals(value) || '').split('.');
  const limitedDecimal = decimal?.slice(0, 2);
  return limitedDecimal == null ? integer : `${integer || 0}.${limitedDecimal}`;
};

export const upToTwoDecimals = (value?: string) => {
  if (!value) return value;
  const [integer, decimal] = value.split('.');
  const twoDecimalPlaces = `${decimal || 0}00`.slice(0, 2);
  return `${integer}.${twoDecimalPlaces}`;
};

export function normalizeEmpty(value?: string): string {
  if (!value) return '0';
  return value;
}

export const formatAMPM = (str: string | Date): string => {
  const date = new Date(str);
  let hours = date.getHours();
  let minutes: string | number = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours %= 12;
  hours = hours || 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? `0${minutes}` : minutes;
  const strTime = `${hours}:${minutes} ${ampm as string}`;
  return strTime;
};

export const formatWithoutAMPM = (str: string | Date): string => {
  const date = new Date(str);
  let hours = date.getHours();
  let minutes: string | number = date.getMinutes();
  hours %= 12;
  hours = hours || 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? `0${minutes}` : minutes;
  const strTime = `${hours}:${minutes}`;
  return strTime;
};

export const stripLeadingZeros = (value?: string) => {
  if (value == null) return value;
  return value.replace(/^0+(?=[^.])(?!$)/g, '');
};

export const withLeadingZeroes = (value?: string | number): string | undefined => {
  if (value == null) return value;
  const valueString = String(value);
  return valueString.length < 2 ? `0${valueString}` : valueString;
};

export const onlyNCharacters = (n: number) => (value?: string) => {
  if (value == null) return value;
  return value.slice(0, n);
};

export const getMinutes = (seconds: number | undefined) => withLeadingZeroes(Math.floor((seconds || 0) / 60));

export const getSeconds = (seconds: number | undefined) => withLeadingZeroes((seconds || 0) % 60);

export const ghsFormatter = buildCurrencyFormatter('GHS');

/* == AMOUNT FORMATTERS == */

export const amountFormatter = (currency: string, focused: boolean) => (value?: string) => {
  const formatter = buildCurrencyFormatter(currency);
  if (value == null || value === '') {
    return null;
  }
  // eslint-disable-next-line react/destructuring-assignment
  const [integer, decimal] = value.split('.');
  let [v] = formatter.format(+integer).split('.');
  v = decimal == null ? v : `${v}.${decimal.slice(0, 2)}`;
  if (!focused) {
    return upToTwoDecimals(v);
  }
  return v;
};

export const parseAmount = (value?: string) => {
  const v = onlyTwoDecimals(value);
  return stripLeadingZeros(v);
};

export const formatDate = (value: string) => {
  if (!value) return value;
  let result = '';
  const [date, time] = value.split('T');
  let hours = Number(time.split(':')[0]);
  let minutes: number | string = Number(time.split(':')[0]);
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours %= 12;
  hours = hours || 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? `0${minutes}` : minutes;
  const strTime = `${hours}:${minutes}${ampm as string}`;

  result = `${strTime} ${date.split('-').reverse().join('-')}`;
  return result;
};

export const getFileSize = (bytes: number) => {
  const size = Math.floor(bytes / 1000);
  let unit = 'kb';
  if (size >= 1024) {
    unit = 'mb';
  }

  return `${size} ${unit}`;
};

export const formatTwoDigitDate = (dateString: string | number) => {
  if (dateString) {
    const createDate = new Date(dateString);
    return createDate.toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }
  return null;
};

export const formattedDate = (dateString: string | number) => {
  if (dateString) {
    const createDate = new Date(dateString);
    const outputDateString = format(createDate, 'MM-dd-yyyy HH:mm:ss');
    return outputDateString;
  }
  return null;
};
